import React, { FC, useEffect, useState } from "react";
import classNames from "classnames";
import { useSelect } from "downshift";

import { DoctorDto } from "@apiModels/DoctorDto.csharp";
import { LocationDto } from "../../../../../Api/Model/LocationDto.csharp";
import { FieldStatus } from "../../Helpers/FieldStatus";
import { LoadingAnimation } from "../../../LoadingAnimation/LoadingAnimation";

type DoctorSelectProps = {
  doctors: DoctorDto[];
  label: string;
  labelSelected: string;
  placeholder: string;
  onSelect: (doctor: DoctorDto) => void;
  doctor: DoctorDto;
  defaultValue: DoctorDto;
  status: FieldStatus;
};

export const DoctorSelect: FC<DoctorSelectProps> = ({
  doctors,
  label,
  labelSelected,
  placeholder,
  onSelect,
  doctor,
  defaultValue,
  status,
}) => {
  const [doctorsToShow, setDoctorsToShow] = useState<DoctorDto[]>([]);
  const {
    isOpen,
    selectedItem,
    getToggleButtonProps,
    getLabelProps,
    getMenuProps,
    highlightedIndex,
    getItemProps,
    selectItem,
  } = useSelect<DoctorDto | null>({ items: doctorsToShow });

  useEffect(() => {
    const activeDoctors = doctors.filter(
      (d) => !!d.id && d.id != "-1" && d.isNotADoctor == false
    );
    const allDoctors = doctors.filter((d) => !!d?.id && d.id != "-1");
    if (
      allDoctors.length == 1 &&
      activeDoctors.length == 1 &&
      !!activeDoctors[0]
    ) {
      selectItem(activeDoctors[0]);
      onSelect(activeDoctors[0]);
    }

    setDoctorsToShow(doctors.filter((d) => d.isNotADoctor == false));
  }, [doctors]);

  useEffect(() => {
    if (!doctor || !doctor.id) {
      selectItem(null);
    }
  }, [doctor]);

  useEffect(() => {
    if (selectedItem != null && selectedItem.id) {
      onSelect(selectedItem);
    }
  }, [selectedItem]);

  const madeSelection =
    selectedItem != null || (defaultValue && defaultValue.id);

  const selectedValueLabel = selectedItem?.name ?? defaultValue?.name ?? "";

  return (
    <div
      className={classNames(
        "position-relative my-2 select booking-narrow-item",
        {
          "select--disabled": status == FieldStatus.disabled,
          "select--error": status == FieldStatus.error,
        }
      )}
    >
      <button
        className="ps-1 py-1 pe-3 select__field"
        type="button"
        {...getToggleButtonProps()}
        disabled={status == FieldStatus.disabled}
      >
        {status == FieldStatus.disabled && madeSelection ? (
          <LoadingAnimation text={selectedValueLabel} />
        ) : (
          selectedValueLabel
        )}
      </button>
      <span
        className="position-absolute select__label select__label--focus"
        {...getLabelProps()}
      >
        {!madeSelection && label}
        {madeSelection && labelSelected}
      </span>
      <span {...getLabelProps()} className="position-absolute select__label">
        {!madeSelection && status == FieldStatus.disabled && (
          <LoadingAnimation text={placeholder} />
        )}
        {!madeSelection && status == FieldStatus.active && placeholder}
      </span>
      <ul className="select__optionsList" {...getMenuProps()}>
        {isOpen && (
          <>
            {doctorsToShow.map((item, index) => (
              <li
                key={item!.id}
                className={classNames("select__option p-1", {
                  "select__option--highlight": highlightedIndex === index,
                })}
                {...getItemProps({ item, index })}
              >
                <p className="m-0">
                  <strong>{item!.name}</strong>
                </p>
                <p className="m-0 font-weight-normal">{item!.jobTitle}</p>
              </li>
            ))}
          </>
        )}
      </ul>
    </div>
  );
};
